import 'mapbox-gl-leaflet'
import '@maplibre/maplibre-gl-leaflet'

import L, { mapboxGL, tileLayer } from 'leaflet'

const baseTileOptions = {
    maxZoom: 20,
    minZoom: 2,
    traceBorderColor: null,
    traceColor: '#000',
}

const vectorLayerOptions = {
    interactive: false,
    updateInterval: 16,
}

const vectorMapRenderOptions = {
    crossSourceCollisions: false,
    fadeDuration: 0,
}

const EMPTY_STYLE = {
    version: 8,
    sources: {},
    layers: [],
}

const styleCache = new Map()
const MAX_STYLE_CACHE_ENTRIES = 20

const getCachedStylePromise = styleUrl => {
    const cached = styleCache.get(styleUrl)
    if (!cached) {
        return null
    }

    // Refresh insertion order to implement LRU behavior.
    styleCache.delete(styleUrl)
    styleCache.set(styleUrl, cached)

    return cached
}

const setCachedStylePromise = (styleUrl, promise) => {
    if (styleCache.has(styleUrl)) {
        styleCache.delete(styleUrl)
    }

    styleCache.set(styleUrl, promise)

    while (styleCache.size > MAX_STYLE_CACHE_ENTRIES) {
        const leastRecentlyUsedKey = styleCache.keys().next().value
        styleCache.delete(leastRecentlyUsedKey)
    }
}

const toAllowOverlap = value => {
    if (typeof value === 'boolean') {
        return value
    }
    return value === 'always'
}

// MapTiler styles can include overlap properties that our current MapLibre runtime
// does not recognize. Normalize them before applying the remote style JSON.
const normalizeLayoutOverlap = style => {
    const normalizedStyle = JSON.parse(JSON.stringify(style || {}))
    normalizedStyle.layers = (normalizedStyle.layers || []).map(layer => {
        if (!layer?.layout) {
            return layer
        }

        const layout = { ...layer.layout }
        let changed = false

        if (Object.prototype.hasOwnProperty.call(layout, 'icon-overlap')) {
            if (!('icon-allow-overlap' in layout)) {
                layout['icon-allow-overlap'] = toAllowOverlap(
                    layout['icon-overlap']
                )
            }
            delete layout['icon-overlap']
            changed = true
        }

        if (Object.prototype.hasOwnProperty.call(layout, 'text-overlap')) {
            if (!('text-allow-overlap' in layout)) {
                layout['text-allow-overlap'] = toAllowOverlap(
                    layout['text-overlap']
                )
            }
            delete layout['text-overlap']
            changed = true
        }

        if (!changed) {
            return layer
        }

        return {
            ...layer,
            layout,
        }
    })

    return normalizedStyle
}

const fetchCompatibleStyle = styleUrl => {
    const cached = getCachedStylePromise(styleUrl)
    if (cached) {
        return cached
    }

    const stylePromise = fetch(styleUrl)
        .then(response => {
            if (!response.ok) {
                throw new Error(`Failed to load style: ${response.status}`)
            }
            return response.json()
        })
        .then(normalizeLayoutOverlap)
        .catch(error => {
            // Avoid retaining rejected promises so future attempts can retry.
            styleCache.delete(styleUrl)
            throw error
        })

    setCachedStylePromise(styleUrl, stylePromise)

    return stylePromise
}

const createMapLibreMapLayer = style => {
    if (typeof style !== 'string') {
        return L.maplibreGL({
            ...vectorLayerOptions,
            ...vectorMapRenderOptions,
            style,
        })
    }

    const layer = L.maplibreGL({
        ...vectorLayerOptions,
        ...vectorMapRenderOptions,
        style: EMPTY_STYLE,
    })

    layer.on('add', () => {
        const glMap = layer.getMaplibreMap?.()
        if (!glMap) {
            return
        }

        fetchCompatibleStyle(style)
            .then(compatibleStyle => {
                if (layer.getMaplibreMap?.()) {
                    glMap.setStyle(compatibleStyle)
                }
            })
            .catch(() => {
                if (layer.getMaplibreMap?.()) {
                    glMap.setStyle(style)
                }
            })
    })

    return layer
}

const MAPTILER_API_KEY = process.env.VUE_APP_MAPTILER_KEY
const createMapTilerStyleUrl = mapId =>
    `https://api.maptiler.com/maps/${mapId}/style.json?key=${MAPTILER_API_KEY}`
const createMapTilerLayer = mapId =>
    createMapLibreMapLayer(createMapTilerStyleUrl(mapId))

const state = {
    addressCoordinates: null,
    forceAlign: false,
    heatmapData: null,
    lastUserPosition: null,
    mapInstance: null,
    navigationRoute: null,
    positionTrackingEnabled: false,
    savedBounds: null,
    tileProviders: [
        {
            id: 'standard',
            createLayer: () =>
                createMapLibreMapLayer(
                    'https://tiles.openfreemap.org/styles/bright'
                ),
            ...baseTileOptions,
            maxZoom: 23,
            traceColor: '#13365e',
        },
        {
            id: 'navigation',
            createLayer: () =>
                mapboxGL({
                    accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
                    style: 'mapbox://styles/mapbox/navigation-day-v1',
                    ...vectorMapRenderOptions,
                    ...vectorLayerOptions,
                }),
            ...baseTileOptions,
            maxZoom: 23,
        },
        {
            id: 'hybrid',
            createLayer: () => createMapTilerLayer('hybrid-v4'),
            ...baseTileOptions,
            maxZoom: 20,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'swisstopo',
            createLayer: () =>
                tileLayer(
                    'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg',
                    { maxZoom: 20, tileSize: 256 }
                ),
            ...baseTileOptions,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'swisstopo-hiking',
            createLayer: () =>
                tileLayer(
                    'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg',
                    { maxZoom: 19, tileSize: 256 }
                ),
            ...baseTileOptions,
            maxZoom: 19,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'light',
            createLayer: () =>
                mapboxGL({
                    accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
                    style: 'mapbox://styles/mapbox/light-v10?optimize=true',
                    ...vectorMapRenderOptions,
                    ...vectorLayerOptions,
                }),
            ...baseTileOptions,
            maxZoom: 23,
            traceBorderColor: '#516677',
            traceColor: '#a4c7f5',
        },
        {
            id: 'dark',
            createLayer: () =>
                mapboxGL({
                    accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
                    style: 'mapbox://styles/mapbox/dark-v10?optimize=true',
                    ...vectorMapRenderOptions,
                    ...vectorLayerOptions,
                }),
            ...baseTileOptions,
            maxZoom: 23,
            traceBorderColor: '#516677',
            traceColor: '#a4c7f5',
        },
    ],
}

const getters = {
    activeTileProvider(state, getters, rootState, rootGetters) {
        const providerId =
            rootGetters['preferences/mapDisplayPreferences']
                .activeTileProviderId
        return (
            state.tileProviders.find(({ id }) => id === providerId) ||
            getters.defaultTileProvider
        )
    },
    defaultTileProvider(state) {
        return process.env.VUE_APP_DEFAULT_MAP
            ? state.tileProviders.find(
                  ({ id }) => id === process.env.VUE_APP_DEFAULT_MAP
              ) || state.tileProviders[0]
            : state.tileProviders[0]
    },
    liveUpdatesEnabledConditionally(state, getters, rootState, rootGetters) {
        return rootGetters['tracker/isTrackerCountHigh']
            ? rootGetters['preferences/mapDisplayPreferences']
                  .liveUpdatesEnabled
            : true
    },
    simpleMarkersEnabledConditionally(state, getters, rootState, rootGetters) {
        return rootGetters['tracker/isTrackerCountHigh']
            ? rootGetters['preferences/mapDisplayPreferences']
                  .simpleMarkersEnabled
            : false
    },
}

const actions = {}

const mutations = {
    addHeatmapData(state, data) {
        if (!state.heatmapData) {
            state.heatmapData = []
        }

        state.heatmapData.push(...data)
    },
    clearSavedBounds(state) {
        state.savedBounds = null
    },
    resetHeatmapData(state) {
        state.heatmapData = null
    },
    saveBounds(state, data) {
        state.savedBounds = data
    },
    setMapInstance(state, data) {
        state.mapInstance = data
    },
    setNavigationRoute(state, data) {
        state.navigationRoute = data
    },
    setPositionTrackingEnabled(state, data) {
        state.positionTrackingEnabled = data
    },
    setUserPosition(state, data) {
        state.lastUserPosition = data
    },
    setForceAlign(state, data) {
        state.forceAlign = data
    },
    setAddressCoordinates(state, data) {
        state.addressCoordinates = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
